import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { fleetLogin as apiLogin, fleetSignup as apiSignup, setToken, clearToken, getFleetDashboard } from '../services/fleetPortalService';

interface FleetUser { id: string; email: string; full_name: string; fleet_id: string; }
interface FleetInfo { id: string; company_name: string; fleet_code?: string; logo_url?: string; }
interface FleetAuthState {
  user: FleetUser | null; fleet: FleetInfo | null; loading: boolean; error: string | null;
  login: (email: string, password: string) => Promise<void>;
  signup: (info: { company_name: string; rfc?: string; contact_name: string; email: string; phone?: string; password: string }) => Promise<void>;
  logout: () => void;
}

const Ctx = createContext<FleetAuthState | null>(null);

export function FleetAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<FleetUser | null>(null);
  const [fleet, setFleet] = useState<FleetInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('nexus_token');
    if (!token) { setLoading(false); return; }
    getFleetDashboard().then((d: any) => { setFleet(d.fleet); setUser({ id: '', email: '', full_name: '', fleet_id: d.fleet?.id }); })
      .catch(() => clearToken()).finally(() => setLoading(false));
  }, []);

  const login = async (email: string, password: string) => {
    setError(null);
    const data = await apiLogin(email, password);
    setToken(data.token);
    setUser(data.user);
    setFleet(data.fleet);
  };

  const signup = async (info: Parameters<FleetAuthState['signup']>[0]) => {
    setError(null);
    const data = await apiSignup(info);
    setToken(data.token);
    setUser(data.user);
    setFleet(data.fleet);
  };

  const logout = () => { clearToken(); setUser(null); setFleet(null); };

  return <Ctx.Provider value={{ user, fleet, loading, error, login, signup, logout }}>{children}</Ctx.Provider>;
}

export function useFleetAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useFleetAuth must be inside FleetAuthProvider');
  return ctx;
}
